<script>
/**
 * @component HealthConversationTemplateEditor
 * @description Editor de plantillas de HealthConversation. Lista las 4
 *              plantillas seed + custom de la org. Permite editar (toggle
 *              activa, ajustar objective/criteria/thresholds/maxTurns,
 *              editar prompts conductor + evaluador con preview de variables,
 *              elegir modelo conductor/evaluador). Solo accesible con
 *              permission `whatsappConfig.edit`.
 *
 *              Las plantillas seed (`isSeed: true`) no se pueden borrar
 *              (solo se toggle activa). Custom sí.
 *
 * @example <HealthConversationTemplateEditor />
 */
import healthConversationTemplateService, {
  ALLOWED_MODELS,
  SEEDED_TEMPLATE_KEYS,
} from "@/services/health-conversation-template";

const SAMPLE_VARS = {
  objective: "Seguimiento día 3 post-operatorio (ejemplo)",
  criteria: "Dolor, Herida, Fiebre, Movilidad",
  maxTurns: 5,
  turnsUsed: 2,
  transcript:
    "Asistente: ¿Cómo estás con el dolor?\nPaciente: Lo voy llevando, 3 de 10.",
  thresholds: JSON.stringify({ green: [1, 3], yellow: [4, 7], red: [8, 10] }),
  closedReason: "ai_sufficient",
};

function renderMustache(template, vars = {}) {
  if (!template) return "";
  return template.replace(/\{\{(\w+)\}\}/g, (_, k) =>
    vars[k] === undefined || vars[k] === null ? "" : String(vars[k])
  );
}

export default {
  name: "HealthConversationTemplateEditor",

  data() {
    return {
      templates: [],
      loading: false,
      saving: false,
      editorOpen: false,
      editing: null,
      previewOpenConductor: false,
      previewOpenEvaluator: false,
      newCriterion: "",
      allowedModels: ALLOWED_MODELS,
    };
  },

  computed: {
    /**
     * @description True si el user puede editar (lee permission del JWT).
     */
    canEdit() {
      const perms = (this.$store.state.user || {}).permissions || [];
      return perms.includes("whatsappConfig.edit");
    },
    /**
     * @description Preview del prompt conductor con variables de ejemplo.
     */
    previewConductor() {
      if (!this.editing) return "";
      return renderMustache(this.editing.conductorPromptTemplate, SAMPLE_VARS);
    },
    previewEvaluator() {
      if (!this.editing) return "";
      return renderMustache(this.editing.evaluatorPromptTemplate, SAMPLE_VARS);
    },
    greenMax: {
      get() {
        return this.editing && this.editing.defaultThresholds
          ? this.editing.defaultThresholds.green[1]
          : 3;
      },
      set(v) {
        this.updateThresholds(v, this.yellowMax);
      },
    },
    yellowMax: {
      get() {
        return this.editing && this.editing.defaultThresholds
          ? this.editing.defaultThresholds.yellow[1]
          : 7;
      },
      set(v) {
        this.updateThresholds(this.greenMax, v);
      },
    },
  },

  mounted() {
    this.load();
  },

  methods: {
    /**
     * @description Devuelve etiqueta legible para un modelo.
     */
    modelLabel(value) {
      const m = ALLOWED_MODELS.find((x) => x.value === value);
      return m ? m.label : value;
    },
    /**
     * @description Devuelve costo estimado en string formateado.
     */
    modelCost(value) {
      const m = ALLOWED_MODELS.find((x) => x.value === value);
      if (!m) return "";
      return `~$${m.costPerCallUsd.toFixed(4)}`;
    },
    /**
     * @description True para plantillas seed (no borrables).
     */
    isSeedKey(key) {
      return SEEDED_TEMPLATE_KEYS.includes(key);
    },
    /**
     * @description Carga plantillas de la org.
     */
    async load() {
      this.loading = true;
      try {
        const { data } = await healthConversationTemplateService.list();
        this.templates = Array.isArray(data) ? data : [];
      } catch (e) {
        this.$message.error("Error cargando plantillas");
      } finally {
        this.loading = false;
      }
    },
    /**
     * @description Abre el modal de edición clonando la plantilla.
     */
    openEditor(template) {
      this.editing = JSON.parse(JSON.stringify(template));
      this.editorOpen = true;
      this.previewOpenConductor = false;
      this.previewOpenEvaluator = false;
    },
    /**
     * @description Crea una plantilla custom desde una seed (clonado).
     */
    async duplicateTemplate(template) {
      const sanitizedKey = `${template.key}_copia_${Date.now()}`;
      const payload = {
        ...template,
        key: sanitizedKey,
        name: `${template.name} (copia)`,
        isSeed: false,
        active: true,
      };
      delete payload._id;
      delete payload.createdAt;
      delete payload.updatedAt;
      try {
        await healthConversationTemplateService.create(payload);
        this.$message.success("Plantilla duplicada");
        this.load();
      } catch (e) {
        this.$message.error("No se pudo duplicar");
      }
    },
    /**
     * @description Borra una plantilla custom. Las seed no se pueden borrar
     *              (botón deshabilitado en UI; backend además lo bloquea).
     */
    async removeTemplate(template) {
      if (template.isSeed) return;
      const ok = await this.$confirm(
        `¿Borrar la plantilla "${template.name}"?`,
        "Confirmar",
        { type: "warning" }
      ).catch(() => false);
      if (!ok) return;
      try {
        await healthConversationTemplateService.remove(template._id);
        this.$message.success("Plantilla eliminada");
        this.load();
      } catch (e) {
        this.$message.error("No se pudo borrar");
      }
    },
    /**
     * @description Toggle activa/inactiva (PUT).
     */
    async toggleActive(template) {
      try {
        await healthConversationTemplateService.update(template._id, {
          active: !template.active,
        });
        this.load();
      } catch (e) {
        this.$message.error("No se pudo actualizar");
      }
    },
    /**
     * @description Guarda los cambios del modal.
     */
    async save() {
      if (!this.editing) return;
      this.saving = true;
      try {
        await healthConversationTemplateService.update(
          this.editing._id,
          this.editing
        );
        this.$message.success("Plantilla guardada");
        this.editorOpen = false;
        this.editing = null;
        this.load();
      } catch (e) {
        this.$message.error("No se pudo guardar");
      } finally {
        this.saving = false;
      }
    },
    /**
     * @description Actualiza scoreThresholds garantizando cobertura [1,10].
     */
    updateThresholds(greenMax, yellowMax) {
      const gMax = Math.max(1, Math.min(greenMax, yellowMax - 1));
      const yMax = Math.max(gMax + 1, Math.min(yellowMax, 9));
      this.editing = {
        ...this.editing,
        defaultThresholds: {
          green: [1, gMax],
          yellow: [gMax + 1, yMax],
          red: [yMax + 1, 10],
        },
      };
    },
    /**
     * @description Agrega criterio default a la plantilla.
     */
    addCriterion() {
      const c = (this.newCriterion || "").trim();
      if (!c) return;
      const exists = (this.editing.defaultCriteria || []).some(
        (x) => x.toLowerCase() === c.toLowerCase()
      );
      if (exists) {
        this.newCriterion = "";
        return;
      }
      this.editing = {
        ...this.editing,
        defaultCriteria: [...(this.editing.defaultCriteria || []), c],
      };
      this.newCriterion = "";
    },
    /**
     * @description Remueve criterio default por índice.
     */
    removeCriterion(idx) {
      const next = [...(this.editing.defaultCriteria || [])];
      next.splice(idx, 1);
      this.editing = { ...this.editing, defaultCriteria: next };
    },
  },
};
</script>

<template lang="pug">
  .health-conversation-template-editor
    .editor__intro
      i.el-icon-info
      span Estas plantillas definen cómo la IA conduce y evalúa las conversaciones de salud. Editalas con criterio clínico.

    el-table(v-loading="loading" :data="templates" border)
      el-table-column(label="Plantilla" min-width="240")
        template(slot-scope="scope")
          .template-row__name
            strong {{ scope.row.name }}
            el-tag(
              v-if="scope.row.isSeed"
              type="primary"
              size="mini"
              style="margin-left: 8px"
            ) Seed
          .template-row__desc {{ scope.row.description }}
      el-table-column(label="Criterios" min-width="160")
        template(slot-scope="scope")
          .template-row__criteria
            el-tag(
              v-for="(c, idx) in scope.row.defaultCriteria || []"
              :key="`c-${idx}`"
              size="mini"
              style="margin-right: 4px"
            ) {{ c }}
      el-table-column(label="Conductor / Evaluador" width="220")
        template(slot-scope="scope")
          .template-row__models
            div {{ modelLabel(scope.row.conductorModel) }}
            div.template-row__models-sep ↓
            div {{ modelLabel(scope.row.evaluatorModel) }}
      el-table-column(label="Activa" width="100")
        template(slot-scope="scope")
          el-switch(
            :value="scope.row.active"
            :disabled="!canEdit"
            @change="toggleActive(scope.row)"
          )
      el-table-column(label="Acciones" width="180")
        template(slot-scope="scope")
          el-button(
            size="mini"
            icon="el-icon-edit"
            :disabled="!canEdit"
            @click="openEditor(scope.row)"
          )
          el-button(
            size="mini"
            icon="el-icon-document-copy"
            :disabled="!canEdit"
            @click="duplicateTemplate(scope.row)"
          )
          el-button(
            size="mini"
            type="danger"
            icon="el-icon-delete"
            :disabled="!canEdit || scope.row.isSeed"
            @click="removeTemplate(scope.row)"
          )

    //- ── MODAL DE EDICIÓN ──
    el-dialog(
      title="Editar plantilla"
      :visible.sync="editorOpen"
      width="780px"
      :close-on-click-modal="false"
    )
      template(v-if="editing")
        el-form(label-position="top")
          el-form-item(label="Nombre")
            el-input(
              :value="editing.name"
              @input="editing = { ...editing, name: $event }"
            )
          el-form-item(label="Descripción")
            el-input(
              :value="editing.description"
              @input="editing = { ...editing, description: $event }"
            )

          el-divider Defaults clínicos

          el-form-item(label="Objetivo default")
            el-input(
              type="textarea"
              :rows="3"
              :value="editing.defaultObjective"
              @input="editing = { ...editing, defaultObjective: $event }"
            )

          el-form-item(label="Criterios default")
            .criteria-chips
              el-tag(
                v-for="(c, idx) in editing.defaultCriteria || []"
                :key="`crit-${idx}`"
                closable
                :disable-transitions="false"
                @close="removeCriterion(idx)"
              ) {{ c }}
              el-input(
                v-model="newCriterion"
                placeholder="+ agregar"
                size="small"
                style="width: 140px"
                @keyup.enter.native="addCriterion"
                @blur="addCriterion"
              )

          el-form-item(label="Umbrales del semáforo")
            .thresholds-row
              .thresholds-row__col
                .thresholds-row__label Verde hasta
                el-input-number(:value="greenMax" :min="1" :max="8" size="small" @input="greenMax = $event")
              .thresholds-row__col
                .thresholds-row__label Amarillo hasta
                el-input-number(:value="yellowMax" :min="2" :max="9" size="small" @input="yellowMax = $event")
              .thresholds-row__col
                .thresholds-row__label Rojo
                .thresholds-row__readonly {{ (yellowMax || 7) + 1 }}–10

          el-form-item(label="Preguntas máximas que el bot puede hacer al paciente")
            el-input-number(
              :value="editing.defaultMaxTurns"
              :min="1"
              :max="15"
              size="small"
              @input="editing = { ...editing, defaultMaxTurns: $event }"
            )
            .form-help-inline Cada pregunta del bot + respuesta del paciente cuenta como un turno. Cuando llega al máximo, la IA cierra la conversación y evalúa con lo que tenga. Recomendado: 5.

          el-divider Prompts y modelos

          el-form-item
            template(slot="label")
              span Modelo CONDUCTOR &nbsp;
              span.model-cost-hint {{ modelCost(editing.conductorModel) }}/turno
            el-select(
              :value="editing.conductorModel"
              style="width: 100%"
              @input="editing = { ...editing, conductorModel: $event }"
            )
              el-option(
                v-for="m in allowedModels"
                :key="m.value"
                :value="m.value"
                :label="m.label"
              )

          el-form-item(label="Prompt conductor")
            .variables-hint
              | Variables disponibles:
              code &#123;&#123;objective&#125;&#125;
              code &#123;&#123;criteria&#125;&#125;
              code &#123;&#123;maxTurns&#125;&#125;
              code &#123;&#123;turnsUsed&#125;&#125;
              code &#123;&#123;transcript&#125;&#125;
            el-input(
              type="textarea"
              :rows="12"
              :value="editing.conductorPromptTemplate"
              @input="editing = { ...editing, conductorPromptTemplate: $event }"
            )
            .preview-toggle(@click="previewOpenConductor = !previewOpenConductor")
              i(:class="previewOpenConductor ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
              span Vista previa con variables de ejemplo
            el-input(
              v-if="previewOpenConductor"
              type="textarea"
              :rows="10"
              :value="previewConductor"
              readonly
              resize="none"
              style="margin-top: 6px"
            )

          el-form-item
            template(slot="label")
              span Modelo EVALUADOR &nbsp;
              span.model-cost-hint {{ modelCost(editing.evaluatorModel) }}/evaluación
            el-select(
              :value="editing.evaluatorModel"
              style="width: 100%"
              @input="editing = { ...editing, evaluatorModel: $event }"
            )
              el-option(
                v-for="m in allowedModels"
                :key="m.value"
                :value="m.value"
                :label="m.label"
              )

          el-form-item(label="Prompt evaluador")
            .variables-hint
              | Variables disponibles:
              code &#123;&#123;criteria&#125;&#125;
              code &#123;&#123;thresholds&#125;&#125;
              code &#123;&#123;transcript&#125;&#125;
              code &#123;&#123;closedReason&#125;&#125;
            el-input(
              type="textarea"
              :rows="12"
              :value="editing.evaluatorPromptTemplate"
              @input="editing = { ...editing, evaluatorPromptTemplate: $event }"
            )
            .preview-toggle(@click="previewOpenEvaluator = !previewOpenEvaluator")
              i(:class="previewOpenEvaluator ? 'el-icon-arrow-down' : 'el-icon-arrow-right'")
              span Vista previa con variables de ejemplo
            el-input(
              v-if="previewOpenEvaluator"
              type="textarea"
              :rows="10"
              :value="previewEvaluator"
              readonly
              resize="none"
              style="margin-top: 6px"
            )

        .snapshot-warning
          i.el-icon-warning-outline
          span Cambios afectan tasks NUEVAS desde el guardado. Tasks ya disparadas mantienen su prompt original (snapshot).

      div(slot="footer")
        el-button(@click="editorOpen = false") Cancelar
        el-button(type="primary" :loading="saving" @click="save") Guardar
</template>

<style lang="scss" scoped>
.health-conversation-template-editor {
  font-family: "Plus Jakarta Sans", sans-serif;
}

.editor__intro {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: #6b7280;
  margin-bottom: 12px;
}

.template-row {
  &__name {
    font-size: 13px;
  }
  &__desc {
    font-size: 12px;
    color: #6b7280;
    margin-top: 2px;
  }
  &__criteria .el-tag {
    margin-bottom: 4px;
  }
  &__models {
    font-size: 12px;
    line-height: 1.3;
  }
  &__models-sep {
    color: #9ca3af;
    text-align: center;
  }
}

.criteria-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  .el-tag {
    margin: 0;
  }
}

.thresholds-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  &__col {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
  &__label {
    font-size: 12px;
    font-weight: 600;
    color: #374151;
  }
  &__readonly {
    height: 32px;
    display: inline-flex;
    align-items: center;
    padding: 0 12px;
    background: #fbe9e7;
    color: #b3261e;
    border-radius: 4px;
    font-weight: 600;
    font-size: 13px;
  }
}

.model-cost-hint {
  font-size: 11px;
  color: #6b7280;
  font-weight: 500;
}

.form-help-inline {
  font-size: 11px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
}

.variables-hint {
  font-size: 11px;
  color: #6b7280;
  margin-bottom: 6px;
  code {
    background: #f3f4f6;
    padding: 1px 5px;
    border-radius: 3px;
    margin: 0 2px;
    font-size: 10px;
  }
}

.preview-toggle {
  margin-top: 6px;
  font-size: 12px;
  cursor: pointer;
  color: #1d3557;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  &:hover {
    color: #0f1e36;
  }
}

.snapshot-warning {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  font-size: 12px;
  color: #b07c0a;
  background: #fbf3e0;
  padding: 8px 12px;
  border-radius: 6px;
  margin-top: 12px;
}
</style>
